import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Loader2, IndianRupee, Plus, Trash2 } from 'lucide-react';

const PAYMENT_MODES = [
  { value: 'neft',           label: 'NEFT / IMPS / RTGS' },
  { value: 'upi',            label: 'UPI (direct)' },
  { value: 'bank_transfer',  label: 'Bank transfer' },
  { value: 'cash',           label: 'Cash' },
  { value: 'cheque',         label: 'Cheque' },
  { value: 'other',          label: 'Other' },
];

const PURPOSES = [
  { value: 'activation', label: 'Activation' },
  { value: 'final',      label: 'Final / Balance' },
  { value: 'full',       label: 'Full package' },
  { value: 'addon',      label: 'Add-on' },
];

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

/**
 * Combined off-gateway payment — ONE bank deposit that covers MULTIPLE
 * line items (e.g. Viva Salon's ₹7,000 NEFT = ₹6,000 balance + ₹1,000
 * add-on). Each item mints its own tax invoice + txn, all sharing the
 * same UTR/reference for Bank Reco.
 */
export const CombinedPaymentDialog = ({ open, onOpenChange, lead, addons = [], onSuccess }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [items, setItems] = useState([
    { purpose: 'final', amount: '', smb_addon_id: '' },
    { purpose: 'addon', amount: '', smb_addon_id: '' },
  ]);
  const [meta, setMeta] = useState({
    payment_mode: 'neft', reference: '', bank_account: '', date: today, notes: '',
  });
  const [banks, setBanks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setItems([
      { purpose: 'final', amount: '', smb_addon_id: '' },
      { purpose: 'addon', amount: '', smb_addon_id: '' },
    ]);
    setMeta({ payment_mode: 'neft', reference: '', bank_account: '', date: today, notes: '' });
    (async () => {
      try {
        const r = await api.get('/transactions/bank-accounts');
        const list = r.data?.data || [];
        setBanks(list);
        if (list.length) setMeta((m) => ({ ...m, bank_account: list[0].id }));
      } catch {
        setBanks([]);
      }
    })();
  }, [open, today]);

  const openAddons = addons.filter((a) => ['draft', 'link_sent'].includes(a.status));
  const total = items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);

  const updateItem = (i, patch) => {
    setItems((prev) => prev.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  };
  const addRow = () => setItems((prev) => [...prev, { purpose: 'addon', amount: '', smb_addon_id: '' }]);
  const removeRow = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    // Validate rows
    const cleaned = [];
    for (const [i, it] of items.entries()) {
      const amt = parseFloat(it.amount);
      if (!amt || amt <= 0) continue;  // skip empty rows silently
      if (it.purpose === 'addon' && !it.smb_addon_id) {
        toast.error(`Row ${i + 1}: pick an add-on for the Add-on purpose`); return;
      }
      cleaned.push({
        purpose: it.purpose,
        amount: amt,
        ...(it.smb_addon_id ? { smb_addon_id: it.smb_addon_id } : {}),
      });
    }
    if (cleaned.length < 2) { toast.error('Add at least 2 line items — use Record Manual Payment for single items'); return; }
    if (!meta.bank_account) { toast.error('Pick the bank account'); return; }

    setBusy(true);
    try {
      const r = await api.post(`/smb/leads/${lead.id}/record-manual-payment-itemized`, {
        items: cleaned,
        payment_mode: meta.payment_mode,
        reference: meta.reference.trim(),
        bank_account: meta.bank_account,
        date: meta.date,
        notes: meta.notes.trim(),
      });
      if (r.data?.success) {
        toast.success(`${r.data.items.length} tax invoices generated · Total ${fmtINR(r.data.total_amount)}`);
        setResult(r.data);
        if (onSuccess) onSuccess(r.data);
      } else {
        toast.error(r.data?.error || 'Failed');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="max-w-xl" data-testid="combined-payment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IndianRupee className="h-4 w-4 text-emerald-500" />
            Record Combined Payment
          </DialogTitle>
          <DialogDescription>
            One bank deposit → multiple line items (each gets its own tax invoice).
            Use this when a single UTR covers balance + add-ons, or multiple add-ons together.
          </DialogDescription>
        </DialogHeader>

        {/* Line items */}
        <div className="space-y-2 border rounded-md p-3">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground pb-1">
            Line Items
          </div>
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-start" data-testid={`combo-row-${i}`}>
              <div className="col-span-4">
                <Select value={it.purpose} onValueChange={(v) => updateItem(i, { purpose: v, smb_addon_id: '' })} disabled={busy}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PURPOSES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-3">
                <Input
                  type="number" min="0" step="0.01"
                  placeholder="Amount"
                  className="h-8 text-xs"
                  value={it.amount}
                  onChange={(e) => updateItem(i, { amount: e.target.value })}
                  disabled={busy}
                />
              </div>
              <div className="col-span-4">
                {it.purpose === 'addon' ? (
                  <Select value={it.smb_addon_id} onValueChange={(v) => updateItem(i, { smb_addon_id: v })} disabled={busy || openAddons.length === 0}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder={openAddons.length ? 'Pick add-on…' : 'No addons'} />
                    </SelectTrigger>
                    <SelectContent>
                      {openAddons.map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.description.slice(0, 30)} — {fmtINR(a.amount)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <span className="text-[10px] text-muted-foreground">Base package line</span>
                )}
              </div>
              <div className="col-span-1 flex justify-end">
                {items.length > 2 && (
                  <Button size="sm" variant="ghost" onClick={() => removeRow(i)} disabled={busy}
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-400">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addRow} disabled={busy} className="w-full h-7 text-xs mt-2">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add Line
          </Button>
          <div className="flex justify-between items-center border-t pt-2 mt-2">
            <span className="text-xs text-muted-foreground">Total</span>
            <span className="font-mono font-semibold text-emerald-400">{fmtINR(total)}</span>
          </div>
        </div>

        {/* Shared payment metadata */}
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Payment mode *</Label>
              <Select value={meta.payment_mode} onValueChange={(v) => setMeta({ ...meta, payment_mode: v })} disabled={busy}>
                <SelectTrigger data-testid="combo-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Date *</Label>
              <Input type="date" value={meta.date} onChange={(e) => setMeta({ ...meta, date: e.target.value })} disabled={busy} data-testid="combo-date" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Bank account *</Label>
            <Select value={meta.bank_account} onValueChange={(v) => setMeta({ ...meta, bank_account: v })} disabled={busy || banks.length === 0}>
              <SelectTrigger data-testid="combo-bank">
                <SelectValue placeholder={banks.length ? 'Select…' : 'No banks configured'} />
              </SelectTrigger>
              <SelectContent>
                {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.name || `${b.bank} — ${b.account_number}`}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">UTR / Reference (shared) *</Label>
            <Input
              value={meta.reference}
              onChange={(e) => setMeta({ ...meta, reference: e.target.value })}
              disabled={busy}
              placeholder="e.g. IMPS-A123456"
              data-testid="combo-reference"
            />
            <div className="text-[10px] text-muted-foreground">
              Applied to every line item — Bank Reco pairs them by this reference.
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea rows={2} value={meta.notes} onChange={(e) => setMeta({ ...meta, notes: e.target.value })} disabled={busy} data-testid="combo-notes" />
          </div>
        </div>

        {result && (
          <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs space-y-1" data-testid="combo-result">
            <div className="font-semibold text-emerald-500">Success · {result.items.length} invoices generated</div>
            {result.items.map((it, i) => (
              <div key={i}>
                <span className="text-muted-foreground">Row {i + 1} ({it.purpose}):</span>{' '}
                <span className="font-mono">{it.invoice_number}</span> · {fmtINR(it.amount)}
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result && (
            <Button onClick={submit} disabled={busy} className="bg-emerald-500 hover:bg-emerald-600" data-testid="combo-submit">
              {busy ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Recording…</> : `Record & Generate ${items.filter((i) => parseFloat(i.amount) > 0).length} Invoices`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CombinedPaymentDialog;
